/**
 * Component for fetching and preparing rate export data
 *
 * Pre-fetches all rate schedules for the given benefit plan groups
 * and calls the onDataReady callback when export rows are built.
 */

import { logger } from "@platform/browser-logger";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { BTX_DEPENDENT_RATE_BASIS } from "@platform/feature-flags";
import {
  listRateSchedules,
  rateQueryKeys,
  BenefitPlanGroup,
} from "@platform/btx-rates";

import { buildRateRowsForAllPlans } from "./rates";

import type { AxiosInstance } from "axios";
import type { RateSchedule } from "@platform/btx-rates";
import type { ExcelRow } from "./rates";

interface RatesDataFetcherProps {
  benefitPlanGroups: BenefitPlanGroup[];
  coverageType?: string;
  axiosInstance: AxiosInstance;
  onDataReady: (rows: ExcelRow[]) => void;
}

/**
 * Fetches rate schedules and builds export data for the given benefit plan groups
 * Calls onDataReady callback when data is ready
 */
export const RatesDataFetcher: React.FC<RatesDataFetcherProps> = ({
  benefitPlanGroups,
  coverageType,
  axiosInstance,
  onDataReady,
}) => {
  const hasCalledOnDataReady = useRef(false);
  const queryClient = useQueryClient();
  const isDependentRateBasisEnabled = useFeatureIsOn(BTX_DEPENDENT_RATE_BASIS);

  const planIds = useMemo(
    () =>
      benefitPlanGroups.flatMap((group) =>
        group.benefitPlans.map((plan) => plan.id),
      ),
    [benefitPlanGroups],
  );

  const cachedRateSchedulesByPlanId = useMemo(() => {
    const map = new Map<string, RateSchedule[]>();

    planIds.forEach((planId) => {
      const cachedSchedules = queryClient.getQueryData<RateSchedule[]>(
        rateQueryKeys.schedules(planId),
      );

      if (cachedSchedules) {
        map.set(planId, cachedSchedules);
      }
    });

    return map;
  }, [planIds, queryClient]);

  const pendingPlanIds = useMemo(
    () =>
      planIds.filter((planId) => {
        if (!cachedRateSchedulesByPlanId.has(planId)) {
          return true;
        }

        return (
          queryClient.getQueryState(rateQueryKeys.schedules(planId))
            ?.fetchStatus === "fetching"
        );
      }),
    [planIds, cachedRateSchedulesByPlanId, queryClient],
  );

  const rateScheduleQueries = useQueries({
    queries: pendingPlanIds.map((planId) => ({
      queryKey: rateQueryKeys.schedules(planId),
      queryFn: () => listRateSchedules(planId, axiosInstance),
      enabled: !!axiosInstance && !!planId,
    })),
  });

  const allDataLoaded = pendingPlanIds.every(
    (_, index) =>
      rateScheduleQueries[index]?.isSuccess &&
      !rateScheduleQueries[index]?.isFetching,
  );

  const rateSchedulesByPlanId = useMemo(() => {
    const map = new Map(cachedRateSchedulesByPlanId);

    pendingPlanIds.forEach((planId, index) => {
      const query = rateScheduleQueries[index];
      if (query.data) {
        map.set(planId, query.data);
      }
    });

    return map;
  }, [cachedRateSchedulesByPlanId, pendingPlanIds, rateScheduleQueries]);

  useEffect(() => {
    if (!allDataLoaded || hasCalledOnDataReady.current) {
      return;
    }

    try {
      const rows = buildRateRowsForAllPlans(
        benefitPlanGroups,
        rateSchedulesByPlanId,
        isDependentRateBasisEnabled,
      );

      logger.info("[btx export] Rate data ready", {
        coverageType,
        rowCount: rows.length,
      });

      hasCalledOnDataReady.current = true;
      onDataReady(rows);
    } catch (error) {
      logger.error("[btx export] Error building rate rows", error as Error);
    }
  }, [
    allDataLoaded,
    benefitPlanGroups,
    rateSchedulesByPlanId,
    coverageType,
    onDataReady,
    isDependentRateBasisEnabled,
  ]);

  return null;
};
